import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GalleryItem } from '@/lib/projects';
import SharedMotionDetailStage, { type SharedDetailHandle } from '@/lib/motion-detail/MotionDetailStage';
import ProductGrid from './golden-product-grid';
import { lockScroll, unlockScroll } from '@/lib/scroll-lock';
import './golden-work.css';

const GROUP_SIZE = 8;
const LEFT_PREVIEW_IDS = [2, 3, 6, 7];
const RIGHT_PREVIEW_IDS = [0, 1, 4, 5];

type GoldenPhotographyWorkProps = { galleryItems: GalleryItem[] };

function PreviewBox({ side, ids, group }: { side: 'left' | 'right'; ids: number[]; group: GalleryItem[] }) {
  return (
    <div className={`product-preview --${side}`}>
      <div className="product-preview__images">
        {ids.filter((i) => group[i]).map((i) => (
          <img key={i} data-id={i} src={group[i].image.src} style={{ objectPosition: group[i].image.focal }} alt="" draggable={false} />
        ))}
      </div>
      <div className="product-preview__details">
        <p className="product-title">title</p>
        <p className="product-price">—</p>
      </div>
      <div className="product-preview__inside masked-preview" />
    </div>
  );
}

/** Visual-only exhibition lines. pointer-events:none, never measured by interaction code. */
function GridLines() {
  return (
    <div className="grid-lines" aria-hidden="true">
      <span className="gl gl-top" />
      <span className="gl gl-v gl-v1 gl-int-left" />
      <span className="gl gl-v gl-v2" />
      <span className="gl gl-v gl-v3 gl-int-right" />
      <span className="gl gl-h gl-h-left gl-int-left" />
      <span className="gl gl-h gl-h-right gl-int-right" />
      {[1, 2, 3].map((k) => (
        <span key={`m${k}`} className="gl gl-h gl-hm" style={{ ['--k' as string]: k, ['--mrows' as string]: 4 }} />
      ))}
    </div>
  );
}

function TailLines({ rows, mrows }: { rows: number; mrows: number }) {
  return (
    <div className="grid-lines" aria-hidden="true" style={{ ['--rows' as string]: rows }}>
      <span className="gl gl-top" />
      <span className="gl gl-v gl-v1" />
      <span className="gl gl-v gl-v2" />
      <span className="gl gl-v gl-v3" />
      {Array.from({ length: rows - 1 }, (_, k) => (
        <span key={k} className="gl gl-h gl-h-tail" style={{ ['--k' as string]: k + 1 }} />
      ))}
      {Array.from({ length: mrows - 1 }, (_, k) => (
        <span key={`m${k}`} className="gl gl-h gl-hm" style={{ ['--k' as string]: k + 1, ['--mrows' as string]: mrows }} />
      ))}
    </div>
  );
}

function Product({ item, localIndex, onOpen }: { item: GalleryItem; localIndex: number; onOpen: (item: GalleryItem, el: HTMLElement) => void }) {
  return (
    <li
      className="product"
      data-gallery-item
      data-name={item.project.title}
      data-price={`${item.project.category} · ${item.project.year}`}
      data-index={localIndex}
      onClick={(e) => onOpen(item, e.currentTarget)}
    >
      <img
        src={item.image.src}
        style={{ objectPosition: item.image.focal }}
        alt={item.image.alt}
        title={`${item.project.title} — ${item.project.category} photography`}
        draggable={false}
      />
    </li>
  );
}

/**
 * GOLDEN Photography Work — the ONE interaction implementation.
 * Rendered by Production Work.
 * Each full 8-item block = one Golden ProductGrid (Codrops DOM, 1 LEFT + 1 RIGHT Preview).
 * One shared Motion Detail Stage for the whole component.
 */
export default function GoldenPhotographyWork({ galleryItems }: GoldenPhotographyWorkProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const gridsRef = useRef<ProductGrid[]>([]);
  const detailRef = useRef<SharedDetailHandle>(null);
  const [inDetail, setInDetail] = useState(false);

  const { blocks, tail } = useMemo(() => {
    const full: GalleryItem[][] = [];
    let i = 0;
    for (; i + GROUP_SIZE <= galleryItems.length; i += GROUP_SIZE) full.push(galleryItems.slice(i, i + GROUP_SIZE));
    return { blocks: full, tail: galleryItems.slice(i) };
  }, [galleryItems]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const blockEls = Array.from(root.querySelectorAll<HTMLElement>('[data-golden-block]'));
    gridsRef.current = blockEls.map((el) => new ProductGrid(el));

    // TEMP dev-only parity invariants.
    let checkTimer: number | null = null;
    const cleanups: (() => void)[] = [];
    if (import.meta.env.DEV && blockEls[0]) {
      const b = blockEls[0];
      console.log('[GoldenParity]', {
        workItems: b.querySelectorAll('[data-gallery-item]').length,
        leftPreviews: b.querySelectorAll('.product-preview.--left').length,
        rightPreviews: b.querySelectorAll('.product-preview.--right').length,
        previewImages: b.querySelectorAll('.product-preview__images > img').length,
        detailStages: document.querySelectorAll('.shared-detail-stage').length,
      });
      const check = () => {
        root.querySelectorAll<HTMLElement>('.product-preview').forEach((preview) => {
          const visible = Array.from(preview.querySelectorAll<HTMLElement>('.product-preview__images > img'))
            .filter((el) => Number(getComputedStyle(el).opacity) > 0.01);
          if (visible.length > 1) console.error('[GoldenParity] MORE THAN ONE PREVIEW IMAGE VISIBLE', visible);
        });
      };
      const onOver = () => {
        if (checkTimer !== null) window.clearTimeout(checkTimer);
        checkTimer = window.setTimeout(check, 250);
      };
      root.addEventListener('mouseover', onOver);
      cleanups.push(() => root.removeEventListener('mouseover', onOver));
    }

    return () => {
      if (checkTimer !== null) window.clearTimeout(checkTimer);
      cleanups.forEach((fn) => fn());
      gridsRef.current.forEach((g) => g.destroy());
      gridsRef.current = [];
    };
  }, [blocks]);

  const openDetail = useCallback((item: GalleryItem, el: HTMLElement) => {
    if (inDetail) return;
    const img = el.querySelector('img');
    if (!img) return;
    lockScroll(); // OPEN START: pin background (visual position unchanged → measurement unchanged)
    setInDetail(true);
    gridsRef.current.forEach((g) => g.beginDetailHandoff());
    detailRef.current?.open(item, img);
  }, [inDetail]);

  const handleClosed = useCallback(() => {
    setInDetail(false);
    gridsRef.current.forEach((g) => g.endDetailHandoff());
    unlockScroll(); // only after the close timeline fully completed
  }, []);

  // Defensive cleanup: never leave a pinned body behind.
  useEffect(() => () => unlockScroll(), []);

  return (
    <div ref={rootRef} className={`golden-work${inDetail ? ' is-detail' : ''}`}>
      {blocks.map((group, bi) => (
        <div key={group[0].id} className={`grid-cell${bi === 0 ? ' is-first' : ''}`} data-grid-cell>
        <div className="products" data-golden-block>
          <ul className="products__grid">
            {group.map((it, i) => <Product key={it.id} item={it} localIndex={i} onOpen={openDetail} />)}
          </ul>
          <div className="products__preview">
            <PreviewBox side="left" ids={LEFT_PREVIEW_IDS} group={group} />
            <PreviewBox side="right" ids={RIGHT_PREVIEW_IDS} group={group} />
          </div>
        </div>
        <GridLines />
        </div>
      ))}
      {tail.length > 0 && (
        <div className={`grid-cell${blocks.length === 0 ? ' is-first' : ''}`} data-grid-cell>
          <ul className="products__tail">
            {tail.map((it, i) => <Product key={it.id} item={it} localIndex={i} onOpen={openDetail} />)}
          </ul>
          <TailLines rows={Math.ceil(tail.length / 4)} mrows={Math.ceil(tail.length / 2)} />
        </div>
      )}
      <SharedMotionDetailStage ref={detailRef} onClosed={handleClosed} />
    </div>
  );
}